'use client';

import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import Loader from '@/components/ui/loader';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'md' | 'lg' | 'xl';
  className?: string;
  dismissOnBackdropClick?: boolean;
  isLoading?: boolean;
}

const sizeClasses: Record<NonNullable<ModalProps['size']>, string> = {
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-3xl',
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  size = 'lg',
  className = '',
  dismissOnBackdropClick = true,
  isLoading = false,
}) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [isLoading, isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  const handleBackdropClick = () => {
    if (dismissOnBackdropClick && !isLoading) {
      onClose();
    }
  };

  const content = (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-4">
      <div
        className="absolute inset-0"
        onClick={handleBackdropClick}
      />
      <div
        className={`relative bg-white rounded-2xl shadow-sm w-full ${sizeClasses[size]} max-h-[90vh] overflow-y-auto`}
      >
        {isLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-white/80 backdrop-blur-sm">
            <Loader className="scale-75" />
          </div>
        )}
        <div className={className}>{children}</div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

